
import { defineComponent, PropType, ref, watch, computed } from 'vue'
import InputKin from '@/views/components/InputKin.vue'
import { TaishakuTaishoKmkType } from '@/repositories/keiei-joho/taishaku-taisho/data'
import { CorporationTypeType, corporationTypeType } from '@/views/corporation-info/types'
import { outputFundTypeType, OutputFundTypeType, taishakuTaishoKmkTypeType as KmkType } from '@/repositories/keiei-joho/data'
import { aggregateAndSet } from '@/views/keiei-joho/taishaku-taisho/uiLogic'
import { formatNumberWithCommas } from '@/views/keiei-joho/mathUtils'

export default defineComponent({
  methods: { formatNumberWithCommas },
  components: { InputKin },
  props: {
    corporationType: {
      type: String as PropType<CorporationTypeType>
    },
    shushikin: {
      type: Object as PropType<TaishakuTaishoKmkType>,
      required: true
    },
    kikin: {
      type: Object as PropType<TaishakuTaishoKmkType>,
      required: true
    },
    outputFundType: {
      type: String as PropType<OutputFundTypeType>,
      required: true
    },
    tsumitateKinItems: {
      type: Array as PropType<TaishakuTaishoKmkType[]>,
      required: true
    },
    hyokaSagakuItems: {
      type: Array as PropType<TaishakuTaishoKmkType[]>,
      required: true
    },
    junshisanGokei: {
      type: Object as PropType<TaishakuTaishoKmkType>,
      required: true
    },
    fusaiGokei: {
      type: Object as PropType<TaishakuTaishoKmkType>,
      required: true
    },
    isDisabledByCorporationType: {
      type: Function as PropType<(item: TaishakuTaishoKmkType) => boolean>,
      required: true
    }
  },
  emits: {
    updateJunshisanGokei: (val: number | null) => true,
    updateShushikin: (val: number | null) => true,
    updateKikin: (val: number | null) => true
  },
  setup (props, { emit }) {
    const localShushikin = computed({
      get: () => props.shushikin.kin,
      set: (value: number | null) => { emit('updateShushikin', value) }
    })
    const localKikin = computed({
      get: () => props.kikin.kin,
      set: (value: number | null) => { emit('updateKikin', value) }
    })

    // 出資金、基金を親へ反映
    watch([localShushikin, localKikin], ([shushiVal, kikinVal]) => {
      const tsumi = props.tsumitateKinItems.find(v => v.kmkType === KmkType.TsumitateKin)?.kin ?? 0
      const hyoka = props.hyokaSagakuItems.find(v => v.kmkType === KmkType.HyokaSagaku)?.kin ?? 0

      if (props.corporationType === corporationTypeType.Type1) {
        const shushikinOrkikin = props.outputFundType === outputFundTypeType.Shushikin ? shushiVal ?? 0 : kikinVal ?? 0
        emit('updateJunshisanGokei', tsumi + hyoka + shushikinOrkikin)
      } else {
        emit('updateJunshisanGokei', tsumi + hyoka + (shushiVal ?? 0) + (kikinVal ?? 0))
      }
    })

    // 法人タイプ１以外の場合の自動計算
    watch(
      [
        () => props.tsumitateKinItems.filter(v => !v.isAggregate),
        () => props.hyokaSagakuItems.filter(v => !v.isAggregate)
      ],
      () => {
        if (props.corporationType === corporationTypeType.Type1) return

        const tsumi = aggregateAndSet(props.tsumitateKinItems, KmkType.TsumitateKin)
        const hyoka = aggregateAndSet(props.hyokaSagakuItems, KmkType.HyokaSagaku)
        const shushikin = localShushikin.value ?? 0
        const kikin = localKikin.value ?? 0
        if (props.junshisanGokei) {
          emit('updateJunshisanGokei', tsumi + hyoka + shushikin + kikin)
        }
      },
      {
        deep: true
      }
    )

    // 法人タイプ１の場合の自動計算
    watch(
      [
        () => props.tsumitateKinItems.find(v => v.kmkType === KmkType.TsumitateKin)?.kin ?? 0,
        () => props.hyokaSagakuItems.find(v => v.kmkType === KmkType.HyokaSagaku)?.kin ?? 0
      ],
      ([tsumi, hyoka]) => {
        if (props.corporationType !== corporationTypeType.Type1) return

        if (props.junshisanGokei) {
          const shushikinOrKikin = props.outputFundType === outputFundTypeType.Shushikin
            ? localShushikin.value
            : localKikin.value
          emit('updateJunshisanGokei', tsumi + hyoka + (shushikinOrKikin ?? 0))
        }
      }
    )

    return {
      corporationTypeType,
      outputFundTypeType,
      localShushikin,
      localKikin
    }
  }
})
